import React from "react";
import { GraduationCap, Globe2, Award, ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LangContext";

// Compact, elegant "What we offer for English" section
// No prices — those live on /pricing
const i18n = {
  ru: {
    kicker: 'Что мы предлагаем',
    title: 'Дальше — ваш путь к языку',
    items: [
      { icon: 'globe', title: 'Языковые школы', desc: 'Великобритания, Мальта, Ирландия — от 2 недель.' },
      { icon: 'award', title: 'IELTS / TOEFL', desc: 'Подготовка с тьютором. Гарантия 7.0+.' },
      { icon: 'graduation', title: 'Foundation Year', desc: 'Год подготовки к университету за рубежом.' }
    ],
    cta: 'Записаться на консультацию',
    note: 'Узнать цены — в разделе «Пакеты и цены»',
    prices: 'Пакеты и цены'
  },
  uk: {
    kicker: 'Що ми пропонуємо',
    title: 'Далі — твій шлях до мови',
    items: [
      { icon: 'globe', title: 'Мовні школи', desc: 'Велика Британія, Мальта, Ірландія — від 2 тижнів.' },
      { icon: 'award', title: 'IELTS / TOEFL', desc: 'Підготовка з тьютором. Гарантія 7.0+.' },
      { icon: 'graduation', title: 'Foundation Year', desc: 'Рік підготовки до університету за кордоном.' }
    ],
    cta: 'Записатися на консультацію',
    note: 'Дізнатися ціни — у розділі «Пакети та ціни»',
    prices: 'Пакети та ціни'
  },
  en: {
    kicker: 'What we offer',
    title: 'Next \u2014 your path to the language',
    items: [
      { icon: 'globe', title: 'Language schools', desc: 'UK, Malta, Ireland — from 2 weeks.' },
      { icon: 'award', title: 'IELTS / TOEFL', desc: 'Tutor-led prep with a guaranteed 7.0+.' },
      { icon: 'graduation', title: 'Foundation Year', desc: 'University bridge year abroad.' }
    ],
    cta: 'Book a consultation',
    note: 'See pricing in the "Packages & Pricing" section',
    prices: 'Packages & Pricing'
  },
  ar: {
    kicker: 'ما الذي نقدّمه',
    title: 'الخطوة التالية \u2014 طريقك إلى اللغة',
    items: [
      { icon: 'globe', title: 'مدارس اللغة', desc: 'المملكة المتحدة، مالطا، أيرلندا \u2014 من أسبوعين.' },
      { icon: 'award', title: 'IELTS / TOEFL', desc: 'تحضير مع مدرّس وضمان 7.0+.' },
      { icon: 'graduation', title: 'Foundation Year', desc: 'سنة تحضير جامعي بالخارج.' }
    ],
    cta: 'احجز استشارة',
    note: 'الأسعار في قسم «الباقات والأسعار»',
    prices: 'الباقات والأسعار'
  },
  it: {
    kicker: 'Cosa offriamo',
    title: 'Prossimo \u2014 il tuo percorso linguistico',
    items: [
      { icon: 'globe', title: 'Scuole di lingua', desc: 'UK, Malta, Irlanda \u2014 da 2 settimane.' },
      { icon: 'award', title: 'IELTS / TOEFL', desc: 'Preparazione con tutor. Garanzia 7.0+.' },
      { icon: 'graduation', title: 'Foundation Year', desc: 'Anno preparatorio universitario all\u2019estero.' }
    ],
    cta: 'Prenota una consulenza',
    note: 'Prezzi nella sezione «Pacchetti e prezzi»',
    prices: 'Pacchetti e prezzi'
  },
  zh: {
    kicker: '我们提供',
    title: '下一步 \u2014 通往语言之路',
    items: [
      { icon: 'globe', title: '语言学校', desc: '英国、马耳他、爱尔兰 \u2014 起 2 周。' },
      { icon: 'award', title: 'IELTS / TOEFL', desc: '导师辅导，保证 7.0+。' },
      { icon: 'graduation', title: 'Foundation Year', desc: '海外大学预备年。' }
    ],
    cta: '预约咨询',
    note: '价格请见「套餐与价格」',
    prices: '套餐与价格'
  },
  es: {
    kicker: 'Qué ofrecemos',
    title: 'Siguiente \u2014 tu camino al idioma',
    items: [
      { icon: 'globe', title: 'Escuelas de idiomas', desc: 'UK, Malta, Irlanda \u2014 desde 2 semanas.' },
      { icon: 'award', title: 'IELTS / TOEFL', desc: 'Preparación con tutor. Garantía 7.0+.' },
      { icon: 'graduation', title: 'Foundation Year', desc: 'Año preparatorio universitario en el extranjero.' }
    ],
    cta: 'Reservar una consulta',
    note: 'Precios en la sección «Paquetes y precios»',
    prices: 'Paquetes y precios'
  },
  pl: {
    kicker: 'Co oferujemy',
    title: 'Dalej \u2014 twoja droga do języka',
    items: [
      { icon: 'globe', title: 'Szkoły językowe', desc: 'UK, Malta, Irlandia \u2014 od 2 tygodni.' },
      { icon: 'award', title: 'IELTS / TOEFL', desc: 'Przygotowanie z tutorem. Gwarancja 7.0+.' },
      { icon: 'graduation', title: 'Foundation Year', desc: 'Rok przygotowawczy na uczelnię za granicą.' }
    ],
    cta: 'Umów konsultację',
    note: 'Ceny w sekcji «Pakiety i ceny»',
    prices: 'Pakiety i ceny'
  }
};

const ICONS = { globe: Globe2, award: Award, graduation: GraduationCap };

const EnglishCourses = ({ onBook }) => {
  const { lang } = useLang();
  const tr = i18n[lang] || i18n.ru;

  return (
    <section className="relative py-14 md:py-20" style={{ background: '#F8F4EE' }} data-testid="english-courses-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-10 md:mb-12 text-center mx-auto">
          <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] uppercase mb-3" style={{ color: '#C9A96E' }}>
            <Sparkles className="w-3 h-3" />
            {tr.kicker}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl leading-[1.1]" style={{ fontFamily: "'Forum', 'Cormorant Garamond', serif", color: '#1B2A4A' }}>
            {tr.title}
          </h2>
        </div>

        {/* 3 minimal cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-10">
          {tr.items.map((c, i) => {
            const Icon = ICONS[c.icon] || GraduationCap;
            return (
              <div
                key={i}
                className="group relative rounded-xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: '#FFFFFF', border: '1px solid rgba(27,42,74,0.08)', boxShadow: '0 4px 16px -8px rgba(27,42,74,0.08)' }}
                data-testid={`course-card-${i}`}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(201,169,110,0.5)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(27,42,74,0.08)'; }}
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,169,110,0.10)', border: '1px solid rgba(201,169,110,0.25)' }}>
                    <Icon className="w-5 h-5" style={{ color: '#C9A96E' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base md:text-[17px] mb-1.5 leading-tight" style={{ color: '#1B2A4A' }}>
                      {c.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#5a6a6a' }}>
                      {c.desc}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Compact dual CTA: book + see prices */}
        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
          <button
            onClick={onBook}
            data-testid="english-book-consult"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold uppercase tracking-[0.12em] transition-all hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, #C9A96E 0%, #D4AF37 100%)',
              color: '#1B2A4A',
              boxShadow: '0 10px 22px -10px rgba(201,169,110,0.45)'
            }}
          >
            {tr.cta}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <a
            href="/pricing"
            className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.12em] transition-colors"
            style={{ color: '#1B2A4A' }}
            data-testid="english-prices-link"
          >
            <span className="border-b border-current pb-0.5">{tr.prices}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
        <p className="text-center text-xs mt-3 italic" style={{ color: '#8a8a8a' }}>
          {tr.note}
        </p>
      </div>
    </section>
  );
};

export default EnglishCourses;
